import React, { useEffect, useRef, useState } from 'react';
import { useGoogleTasksImport, ImportCandidateView } from '../hooks/useGoogleTasksImport';
import { InferenceConfidence } from '../domain';
import { Alert, Badge, Button, Checkbox, Input, Select, Tone, focusRing, cx } from './ui';

const CONFIDENCE_TONES: Record<InferenceConfidence, Tone> = {
  high: 'success',
  medium: 'warning',
  low: 'danger',
  none: 'neutral',
};

/** Checkbox with an indeterminate state, which React can only set imperatively. */
const TriStateCheckbox: React.FC<{
  checked: boolean;
  indeterminate: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}> = ({ checked, indeterminate, disabled, onChange, label }) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <Checkbox
      ref={ref}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
    />
  );
};

const CandidateRow: React.FC<{
  candidate: ImportCandidateView;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
}> = ({ candidate, expanded, onToggleExpand, onToggleSelect }) => (
  <>
    <tr className="border-t border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/40">
      <td className="px-4 py-3 align-top">
        <Checkbox
          checked={candidate.selected}
          disabled={candidate.imported}
          onChange={onToggleSelect}
          aria-label={`Select ${candidate.title}`}
        />
      </td>
      <td className="px-4 py-3 align-top">
        <button
          onClick={onToggleExpand}
          aria-expanded={expanded}
          className={cx('rounded text-left font-medium text-gray-900 hover:text-indigo-700 dark:text-gray-100 dark:hover:text-indigo-300', focusRing)}
        >
          {candidate.title}
        </button>
        {candidate.imported && (
          <Badge tone="success" className="ml-2">imported</Badge>
        )}
        {!candidate.imported && candidate.alreadyExists && (
          <Badge
            tone="warning"
            className="ml-2"
            title="A chore with this name already exists in the selected household. You can still import it, but it will create a second one."
          >
            already exists
          </Badge>
        )}
        {!candidate.imported && !candidate.alreadyExists && candidate.hasOpenInstance && (
          <Badge tone="brand" className="ml-2">open</Badge>
        )}
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{candidate.listTitle}</p>
      </td>
      <td className="px-4 py-3 align-top text-sm tabular-nums text-gray-700 dark:text-gray-300">
        {candidate.occurrenceCount}
      </td>
      <td className="px-4 py-3 align-top text-sm tabular-nums text-gray-700 dark:text-gray-300">
        {candidate.gapLabel || <span className="text-gray-400 dark:text-gray-400">—</span>}
      </td>
      <td className="px-4 py-3 align-top text-sm text-gray-900 dark:text-gray-100">{candidate.scheduleLabel}</td>
      <td className="px-4 py-3 align-top">
        <Badge tone={CONFIDENCE_TONES[candidate.inference.confidence]}>
          {candidate.confidenceLabel}
        </Badge>
      </td>
    </tr>
    {expanded && (
      <tr className="border-t border-gray-50 bg-gray-50 dark:border-gray-800 dark:bg-gray-950/50">
        <td colSpan={6} className="px-4 py-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">{candidate.inference.rationale}</p>
          {candidate.occurrenceLabels.length > 0 && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Occurrences:</span>{' '}
              {candidate.occurrenceLabels.join(' · ')}
            </p>
          )}
          {candidate.inference.gapsInDays.length > 0 && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Gaps (days):</span>{' '}
              {candidate.inference.gapsInDays.join(' · ')}
            </p>
          )}
          {candidate.notes && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Notes:</span> {candidate.notes}
            </p>
          )}
          <pre className="mt-2 overflow-x-auto rounded bg-white p-2 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
            {JSON.stringify(candidate.inference.schedule, null, 2)}
          </pre>
        </td>
      </tr>
    )}
  </>
);

const CandidateTable: React.FC<{
  candidates: ImportCandidateView[];
  expandedKeys: Set<string>;
  onToggleExpand: (key: string) => void;
  onToggleSelect: (key: string) => void;
  onToggleAll: (selected: boolean) => void;
  allSelected: boolean;
  someSelected: boolean;
  hasSelectable: boolean;
}> = ({
  candidates, expandedKeys, onToggleExpand, onToggleSelect, onToggleAll,
  allSelected, someSelected, hasSelectable,
}) => (
  <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:shadow-none">
    <table className="min-w-full text-left">
      <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
        <tr>
          <th scope="col" className="px-4 py-2 font-medium">
            <TriStateCheckbox
              checked={allSelected}
              indeterminate={someSelected}
              disabled={!hasSelectable}
              onChange={() => onToggleAll(!allSelected)}
              label="Select all in this section"
            />
          </th>
          <th scope="col" className="px-4 py-2 font-medium">Task</th>
          <th scope="col" className="px-4 py-2 font-medium">Occurrences</th>
          <th scope="col" className="px-4 py-2 font-medium">Typical gap</th>
          <th scope="col" className="px-4 py-2 font-medium">Inferred schedule</th>
          <th scope="col" className="px-4 py-2 font-medium">Confidence</th>
        </tr>
      </thead>
      <tbody>
        {candidates.map((candidate) => (
          <CandidateRow
            key={candidate.key}
            candidate={candidate}
            expanded={expandedKeys.has(candidate.key)}
            onToggleExpand={() => onToggleExpand(candidate.key)}
            onToggleSelect={() => onToggleSelect(candidate.key)}
          />
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Experimental Google Tasks import.
 *
 * Lists candidates with a schedule inferred from occurrence history, and imports the
 * selected ones as chores. The inference is a guess — the expandable rows exist so the
 * guess can be checked before anything is created.
 */
export const ImportPage: React.FC = () => {
  const {
    recurring, oneOffs, recurringTotal, oneOffsTotal, oneOffsAllTotal, oneOffsOpenTotal,
    renderLimit, search, setSearch, showOneOffs, setShowOneOffs, openOneOffsOnly,
    setOpenOneOffsOnly, truncatedLists, scanning, scanned, error, listCount, taskCount,
    scan, reset, clearError, households, householdId, setTargetHouseholdId, selectedCount,
    selectedDuplicateCount, toggleSelection, toggleSection, sectionState, clearSelection,
    canImport, importing, importResult, importToTarget, dismissImportResult,
  } = useGoogleTasksImport();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggleExpand = (key: string) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Counts the unfiltered one-offs on purpose: the open-only filter must not make the
  // page claim there are no candidates, nor hide the search and import controls.
  const totalCandidates = recurringTotal + oneOffsAllTotal;

  const renderSection = (
    title: string,
    description: string,
    section: ImportCandidateView[],
    total: number,
  ) => {
    // Select-all acts on the rows actually on screen, not the hidden remainder — the
    // safe reading of the control, and the reason the cap notice mentions search.
    const { allSelected, someSelected, hasSelectable } = sectionState(section);
    return (
      <section className="space-y-2">
        {title && (
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title} ({total})
          </h2>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
        {total > section.length && (
          <Alert tone="warning">
            Showing the first {renderLimit} of {total}. Use the search box to narrow this down —
            select-all only applies to the rows shown.
          </Alert>
        )}
        <CandidateTable
          candidates={section}
          expandedKeys={expandedKeys}
          onToggleExpand={toggleExpand}
          onToggleSelect={toggleSelection}
          onToggleAll={(selected) => toggleSection(section, selected)}
          allSelected={allSelected}
          someSelected={someSelected}
          hasSelectable={hasSelectable}
        />
      </section>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Import from Google Tasks</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Experimental. Reads your Google Tasks, guesses a schedule for each one, and imports the
          ones you pick as chores.
        </p>
      </div>

      <Alert tone="info" title="Why these are guesses">
        <p>
          The Google Tasks API exposes no recurrence field — recurrence exists in Google&apos;s own
          app but was never added to the public API, and Takeout omits it too. What we can see is
          that each occurrence of a recurring task is stored as a separate record with the same
          title, so the schedule below is reconstructed from the gaps between those dates. Check a
          row before importing it.
        </p>
      </Alert>

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={scan} loading={scanning} disabled={importing}>
          {scanning ? 'Scanning…' : scanned ? 'Rescan Google Tasks' : 'Scan Google Tasks'}
        </Button>
        {scanned && (
          <Button onClick={reset} disabled={importing}>
            Clear
          </Button>
        )}
        {scanned && !scanning && (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {taskCount} task{taskCount === 1 ? '' : 's'} across {listCount} list
            {listCount === 1 ? '' : 's'} → {totalCandidates} candidate
            {totalCandidates === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {error && (
        <Alert tone="error" action={<Button variant="ghost" size="sm" onClick={clearError}>Dismiss</Button>}>
          {error}
        </Alert>
      )}

      {importResult && (
        <Alert
          tone={importResult.failures.length > 0 ? 'warning' : 'success'}
          action={<Button variant="ghost" size="sm" onClick={dismissImportResult}>Dismiss</Button>}
        >
          <p>
            Imported {importResult.imported} chore
            {importResult.imported === 1 ? '' : 's'}.
            {importResult.failures.length > 0 &&
              ` ${importResult.failures.length} failed — still selected, so you can retry.`}
          </p>
          {importResult.failures.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {importResult.failures.map((failure) => (
                <li key={failure.title}>
                  <span className="font-medium">{failure.title}</span>: {failure.error}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {truncatedLists.length > 0 && (
        <Alert tone="warning" title="Partial history">
          <p>
            {truncatedLists.join(', ')} {truncatedLists.length === 1 ? 'holds' : 'hold'} more tasks
            than a single scan reads, so some occurrences were not seen. Intervals inferred from
            these lists may be based on incomplete history.
          </p>
        </Alert>
      )}

      {scanned && totalCandidates > 0 && (
        <div className="sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-10 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:shadow-none">
          <Input
            compact
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tasks…"
            aria-label="Search candidates by title"
            className="w-full sm:w-64"
          />

          <label htmlFor="import-household" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Import into
          </label>
          <Select
            compact
            id="import-household"
            value={householdId}
            onChange={(event) => setTargetHouseholdId(event.target.value)}
            disabled={importing}
          >
            {households.length === 0 && <option value="">No households yet</option>}
            {households.map((entry) => (
              <option key={entry.household.id} value={entry.household.id}>
                {entry.household.name}
              </option>
            ))}
          </Select>

          <span className="text-sm text-gray-600 dark:text-gray-400">
            {selectedCount} selected
          </span>

          {selectedCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearSelection} disabled={importing}>
              Clear selection
            </Button>
          )}

          <Button variant="success" onClick={importToTarget} disabled={!canImport} className="ml-auto">
            {importing
              ? 'Importing…'
              : `Import ${selectedCount || ''} ${selectedCount === 1 ? 'chore' : 'chores'}`.trim()}
          </Button>

          {households.length === 0 && (
            <p className="w-full text-sm text-amber-700 dark:text-amber-400">
              Create a household first — imported chores need somewhere to live.
            </p>
          )}

          {selectedDuplicateCount > 0 && (
            <p className="w-full text-sm text-amber-700 dark:text-amber-400">
              {selectedDuplicateCount} selected{' '}
              {selectedDuplicateCount === 1 ? 'task matches a chore' : 'tasks match chores'} that
              already {selectedDuplicateCount === 1 ? 'exists' : 'exist'} in this household —
              importing will create {selectedDuplicateCount === 1 ? 'a duplicate' : 'duplicates'}.
            </p>
          )}
        </div>
      )}

      {scanned && recurringTotal > 0 && renderSection(
        'Recurring',
        'A repeating rhythm was detected. Select a row to see the dates behind the guess.',
        recurring,
        recurringTotal,
      )}

      {scanned && oneOffsAllTotal > 0 && (
        <section className="space-y-2">
          <button
            onClick={() => setShowOneOffs(!showOneOffs)}
            aria-expanded={showOneOffs}
            className={cx('flex items-center gap-2 rounded text-lg font-semibold text-gray-900 hover:text-indigo-700 dark:text-gray-100 dark:hover:text-indigo-300', focusRing)}
          >
            <span className="text-sm text-gray-500 dark:text-gray-400">{showOneOffs ? '▾' : '▸'}</span>
            No rhythm detected ({oneOffsAllTotal})
          </button>
          {!showOneOffs && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Single-occurrence tasks — mostly ordinary to-dos that were never recurring chores.
              {oneOffsOpenTotal > 0 && ` ${oneOffsOpenTotal} of them are still open.`} Expand to
              import some anyway.
            </p>
          )}
          {showOneOffs && (
            <>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Checkbox
                  checked={openOneOffsOnly}
                  onChange={() => setOpenOneOffsOnly(!openOneOffsOnly)}
                />
                Only still-open tasks ({oneOffsOpenTotal} of {oneOffsAllTotal})
              </label>
              {oneOffsTotal > 0 ? renderSection(
                '',
                openOneOffsOnly
                  ? 'Open tasks with no measurable interval — pending to-dos rather than finished history. These import as one-off chores; edit the schedule afterwards if they should repeat.'
                  : 'Too few dated occurrences to measure an interval. These import as one-off chores — edit the schedule afterwards if they should repeat.',
                oneOffs,
                oneOffsTotal,
              ) : (
                <p className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600 shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:shadow-none">
                  No open one-off tasks. Untick the filter to see completed ones.
                </p>
              )}
            </>
          )}
        </section>
      )}

      {scanned && !scanning && totalCandidates === 0 && (
        <p className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600 shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:shadow-none">
          {search.trim()
            ? 'No candidates match your search.'
            : 'No tasks found. If you expected some, check that the Google account you authorised is the one holding your lists.'}
        </p>
      )}
    </div>
  );
};
